import React from 'react';
import { Loader2 } from 'lucide-react';
import { useResponsive } from '../../contexts/ResponsiveContext';

interface GoogleSignInButtonProps {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  text?: string;
  className?: string;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onClick,
  loading = false,
  disabled = false,
  text = 'Continue with Google',
  className = ''
}) => {
  const { isMobile } = useResponsive();
  
  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
  
  // Match the exact sizing of the Button component's 'lg' size
  const sizeClasses = 'px-8 py-4 text-lg';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        w-full relative overflow-hidden font-heading font-medium rounded-full
        transition-all duration-300 hover:shadow-sm hover:scale-[1.02] active:scale-[0.98]
        flex items-center justify-center gap-2 isolate group
        focus:outline-none focus:ring-2 focus:ring-gray-400/50 focus:ring-offset-2
        focus:ring-offset-background-light dark:focus:ring-offset-background-dark
        ${sizeClasses}
        bg-surface-light dark:bg-surface-dark
        border border-border-light dark:border-border-dark
        text-text-primary-light dark:text-text-primary-dark
        shadow-sm hover:shadow-md
        hover:bg-surface-light/50 dark:hover:bg-surface-dark/50
        hover:border-primary-light dark:hover:border-primary-dark
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      <span className="absolute inset-0 w-[100%] bg-gradient-to-r from-transparent via-gray-100 to-transparent opacity-0 -translate-x-full group-hover:translate-x-full group-hover:opacity-20 transition-all duration-1000 ease-out"></span>
      {loading ? (
        <Loader2 size={20} className="animate-spin text-gray-600" />
      ) : (
        <GoogleIcon />
      )}
      <span className={`${isMobile ? 'text-lg' : 'text-lg'} font-medium`}>
        {loading ? 'Signing in...' : text}
      </span>
    </button>
  );
};